/**
 * AvatarListItem - Avatar variant design
 *
 * Renders initials-based list items with timestamps and badges.
 * Used internally by CList when variant="avatar".
 */
"use client";

import React from "react";
import { cn } from "@/lib/utils";
import type { AvatarItemType, AvatarBadgeVariant } from "./types";
import { AVATAR_BADGE_CONFIG } from "./types";

interface AvatarListItemProps {
  items: AvatarItemType[];
  showDivider?: boolean;
}

const AvatarListItem: React.FC<AvatarListItemProps> = ({
  items,
  showDivider = true,
}) => {
  return (
    <div className="flex flex-col">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const initialsColor =
          item.initialsColor || "bg-primary/10 text-primary";
        const badgeVariant: AvatarBadgeVariant =
          item.badge?.variant || "neutral";

        return (
          <div
            key={item.id || index}
            className={cn(
              "flex items-center py-2.5",
              showDivider &&
                !isLast &&
                "border-b border-slate-200 dark:border-slate-700",
              item.onClick &&
                "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors rounded-md px-1 -mx-1",
            )}
            onClick={item.onClick}
          >
            {/* Avatar Initials */}
            <div
              className={cn(
                "w-9 h-9 rounded-md flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0",
                initialsColor,
              )}
            >
              {item.initials}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-slate-900 dark:text-slate-100 truncate">
                {item.title}
              </div>
              {item.subtitle && (
                <div className="text-xs text-slate-400 dark:text-slate-500 truncate">
                  {item.subtitle}
                </div>
              )}
            </div>

            {/* Badge & Timestamp */}
            {(item.badge || item.timestamp) && (
              <div className="text-right ml-2 flex-shrink-0">
                {item.badge && (
                  <span
                    className={cn(
                      "text-[10px] font-semibold uppercase px-2 py-0.5 rounded",
                      AVATAR_BADGE_CONFIG[badgeVariant],
                    )}
                  >
                    {item.badge.label}
                  </span>
                )}
                {item.timestamp && (
                  <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                    {item.timestamp}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {items.length === 0 && (
        <div
          className={cn(
            "py-8 text-center text-sm",
            "text-slate-400 dark:text-gray-500",
          )}
        >
          No items to display
        </div>
      )}
    </div>
  );
};

export default AvatarListItem;
